import { useCallback, useEffect } from "react";
import Patient from "../../../Utility/Crud/Patient";
import User from "../../../Utility/Crud/User";

/**
 * Encapsulates create-ticket draft building, reconciliation with the chart,
 * navigation into ticket routes, and route-driven effects (refresh sync and
 * invalid draft redirect).
 *
 * @param {Object} parameters
 * @param {Object|null} parameters.chart - Current chart state.
 * @param {Object|undefined} parameters.patient - Current patient record for the chart.
 * @param {number|null} parameters.patientId - Active patient identifier.
 * @param {Object} parameters.mainContext - Main application context (users, current user).
 * @param {function(string): string} parameters.formatOrderType - Maps route order or ticket type strings to stored labels.
 * @param {function(Object): void} parameters.onChange - Merges chart fragment updates into chart state.
 * @param {function(): number} parameters.getNegativeId - Returns the next temporary negative entity identifier.
 * @param {number|null} parameters.orderId - Currently focused order or draft order identifier.
 * @param {function(number|null): void} parameters.setOrderId - Sets the focused order identifier.
 * @param {string|null} parameters.pageMode - Route-derived page mode segment.
 * @param {boolean} parameters.isChartLoading - True while the chart is loading from the server.
 * @param {import("react-router").Location} parameters.location - React Router location (pathname and state).
 * @param {import("history").History} parameters.history - React Router history for redirects.
 * @param {string[]} parameters.ticketPageModes - Page mode values that represent create-ticket flows.
 * @param {number|null} parameters.routeParentOrderId - Parent order id parsed directly from the route.
 * @param {function(string, Object|null): void} parameters.handleNavigateChart - Navigation helper with unsaved-change handling.
 * @param {function(): boolean} parameters.hasPendingChanges - Checks if chart has pending changes.
 * @param {boolean} parameters.setShowSaveDialog - Shows the save dialog if we have pending changes.
 * @param {string} parameters.setPendingNavigatePath - Sets the path to navigate to on dialog save.
 * @returns {{ prepareTicketData: function(Object, string, Object|null): void }}
 */
export function useChartCreateTicketFlow({
  chart,
  patient,
  patientId,
  mainContext,
  formatOrderType,
  onChange,
  getNegativeId,
  orderId,
  setOrderId,
  pageMode,
  isChartLoading,
  location,
  history,
  ticketPageModes,
  routeParentOrderId,
  handleNavigateChart,
  hasPendingChanges,
  setShowSaveDialog,
  setPendingNavigatePath,
}) {
  /**
   * Builds the create ticket data.
   * @param {Object} order - The order object.
   * @param {string} ticketType - The ticket type.
   * @param {Object|null} ticketRouteData - The ticket route data.
   * @returns {Object} The create ticket data.
   */
  const buildCreateTicketData = useCallback(
    (order, ticketType, ticketRouteData = null) => {
      const newActivityId = getNegativeId();
      const newApprovalId = getNegativeId();
      const newServiceOrderReasonId = getNegativeId();
      const routeOrderStatusReasonId =
        ticketRouteData?.orderStatusReasonId ?? null;
      const resolveOwnerUserId = () => {
        if (ticketType === "service" || ticketType === "resupply") {
          return 211;
        }
        return (
          Patient.getSalesRepUserprofile(chart, mainContext.usersIndexed)?.id ??
          mainContext.currentUser?.id ??
          211
        );
      };

      const resolveSalesAndServiceLocationId = () => {
        if (ticketType === "service" || ticketType === "resupply") {
          return 45;
        }
        return order.serviceLocationId;
      };

      const salesAndServiceLocationId = resolveSalesAndServiceLocationId();
      let billingLocationId = order.billingLocationId;
      if (
        ticketType === "service" &&
        mainContext.locationsIndexed &&
        mainContext.locationsIndexed[salesAndServiceLocationId] &&
        mainContext.locationsIndexed[salesAndServiceLocationId]
          .billingLocationId
      ) {
        billingLocationId =
          mainContext.locationsIndexed[salesAndServiceLocationId]
            .billingLocationId;
      }

      const newActivity = {
        [newActivityId]: {
          id: newActivityId,
          type: "Ticket",
          accountId: patient.accountId,
          startedAt: new Date().toISOString(),
          endedAt: new Date().toISOString(),
          quality: false,
          modified: new Date().toISOString(),
          priority: 0,
          isExpense: false,
          ownerId: resolveOwnerUserId(),
        },
      };

      const newOrderActivity = {
        [newActivityId]: {
          id: newActivityId,

          orderType: formatOrderType(ticketType),
          orderStatusReasonId: routeOrderStatusReasonId,

          insuranceTypeId: patient.insuranceTypeId,
          insuranceSubTypeId: patient.insuranceSubTypeId,
          insurancePolicy: patient.insurancePolicy,
          insuranceGroup: patient.insuranceGroup,

          secondaryInsuranceTypeId: patient.secondaryInsuranceTypeId,
          secondaryInsuranceSubTypeId: patient.secondaryInsuranceSubTypeId,
          secondaryInsurancePolicy: patient.secondaryInsurancePolicy,
          secondaryInsuranceGroup: patient.secondaryInsuranceGroup,

          lastUpdatedOn: new Date().toISOString(),
          lastUpdatedById: mainContext.currentUser.id,
          patientId: patientId,
          patientName: Patient.getDisplayName(patient),
          lastStatusChange: new Date().toISOString(),

          phone: patient.phone,
          address: patient.address,
          city: patient.city,
          state: patient.state,
          zipCode: patient.zipCode,
          dateOfBirth: patient.dateOfBirth,
          weight: patient.weight,
          gender: patient.gender,
          email: patient.email,
          firstDiagnosisCodeId: patient.firstDiagnosisCodeId,
          secondDiagnosisCodeId: patient.secondDiagnosisCodeId,
          thirdDiagnosisCodeId: patient.thirdDiagnosisCodeId,
          fourthDiagnosisCodeId: patient.fourthDiagnosisCodeId,
          orderingProviderId: patient.orderingProviderId,
          orderingProviderNotFound: patient.orderingProviderNotFound,
          jsonOrderingProviderNotFoundData:
            patient.jsonOrderingProviderNotFoundData,
          hasInfectiousDisease: patient.hasInfectiousDisease,
          diseaseNotes: patient.diseaseNotes,

          serviceLocationId: salesAndServiceLocationId,
          salesLocationId: salesAndServiceLocationId,
          billingLocationId: billingLocationId,

          products: [],
        },
      };

      const resolvedOwnerUserId = resolveOwnerUserId();

      const serviceOrderReason = {
        [newServiceOrderReasonId]: {
          id: newServiceOrderReasonId,
          previousOrderId: order.id,
          currentOrderId: newActivityId,
          name: Patient.getDisplayName(patient),
          technician: order.technician
            ? order.technician.name
            : "No Technician",
          salesRep: resolvedOwnerUserId
            ? User.getDisplayName(mainContext.usersIndexed[resolvedOwnerUserId])
            : "No Sales Rep",
          atp: order.atp ? order.atp.name : "No ATP",
          serviceReason: "",
        },
      };

      const approval = {
        [newApprovalId]: {
          id: newApprovalId,
        },
      };

      const orderActivityApprovals = {
        [newApprovalId]: {
          orderActivityId: newActivityId,
          approvalsId: newApprovalId,
        },
      };

      return {
        createOrderId: newActivityId,
        chartChange: {
          activity: newActivity,
          orderActivity: newOrderActivity,
          serviceOrderReason: serviceOrderReason,
          approval: approval,
          orderActivityApprovals: orderActivityApprovals,
        },
      };
    },
    [
      chart,
      formatOrderType,
      getNegativeId,
      mainContext.currentUser?.id,
      mainContext.usersIndexed,
      mainContext.locationsIndexed,
      patient,
      patientId,
    ],
  );

  /**
   * Ensures the create ticket data.
   * @param {number} parentOrderId - The parent order id.
   * @param {string} ticketType - The ticket type.
   * @param {Object|null} ticketRouteData - The ticket route data.
   * @returns {number|null} The create ticket data.
   */
  const ensureCreateTicket = useCallback(
    (parentOrderId, ticketType, ticketRouteData = null) => {
      const formattedTicketType = formatOrderType(ticketType);
      const existingOrderId =
        Object.values(chart?.orderActivity || {}).find((orderActivity) => {
          if (
            !orderActivity ||
            orderActivity.id >= 0 ||
            orderActivity.orderType !== formattedTicketType
          ) {
            return false;
          }

          return Object.values(chart?.serviceOrderReason || {}).some(
            (serviceReason) =>
              serviceReason?.currentOrderId === orderActivity.id &&
              serviceReason?.previousOrderId === parentOrderId,
          );
        })?.id ?? null;

      if (existingOrderId !== null) {
        return existingOrderId;
      }

      const parentOrder = chart?.orderActivity?.[parentOrderId];
      if (!parentOrder || !patient) {
        return null;
      }

      const { createOrderId, chartChange } = buildCreateTicketData(
        parentOrder,
        ticketType,
        ticketRouteData,
      );
      onChange(chartChange);

      return createOrderId;
    },
    [
      buildCreateTicketData,
      chart?.orderActivity,
      chart?.serviceOrderReason,
      formatOrderType,
      onChange,
      patient,
    ],
  );

  /**
   * Prepares the ticket data.
   * @param {Object} order - The order object.
   * @param {string} ticketType - The ticket type.
   * @param {Object|null} ticketRouteData - The ticket route data.
   * @returns {void}
   */
  const prepareTicketData = useCallback(
    (order, ticketType, ticketRouteData = null) => {
      if (hasPendingChanges) {
        setPendingNavigatePath(
          `/chart/${patientId}/order/${order.id}/${ticketType}`,
          ticketRouteData ? { ticketRouteData } : null,
        );
        setShowSaveDialog(true);
        return;
      }
      const createdTicketOrderId = ensureCreateTicket(
        order.id,
        ticketType,
        ticketRouteData,
      );
      if (createdTicketOrderId !== null) {
        setOrderId(createdTicketOrderId);
      }

      handleNavigateChart(
        `/chart/${patientId}/order/${order.id}/${ticketType}`,
        ticketRouteData ? { ticketRouteData } : null,
      );
    },
    [ensureCreateTicket, handleNavigateChart, patientId, setOrderId],
  );

  /**
   * Handles the creation of a new ticket.
   * @returns {void}
   */
  useEffect(() => {
    if (
      isChartLoading ||
      !chart ||
      !patient ||
      !pageMode ||
      !ticketPageModes.includes(pageMode) ||
      !routeParentOrderId ||
      routeParentOrderId <= 0
    ) {
      return;
    }

    const createTicketOrderId = ensureCreateTicket(
      routeParentOrderId,
      pageMode,
      location.state?.ticketRouteData ?? null,
    );
    if (createTicketOrderId !== null && createTicketOrderId !== orderId) {
      setOrderId(createTicketOrderId);
    }
  }, [
    chart,
    ensureCreateTicket,
    isChartLoading,
    location.state,
    orderId,
    pageMode,
    patient,
    routeParentOrderId,
    ticketPageModes,
    setOrderId,
  ]);

  /**
   * Handles the navigation to the patient chart page.
   * @returns {void}
   */
  useEffect(() => {
    if (
      !isChartLoading &&
      !!chart &&
      !!orderId &&
      !!pageMode &&
      ticketPageModes.includes(pageMode) &&
      orderId < 0 &&
      !chart.orderActivity?.[orderId] &&
      !!patientId
    ) {
      history.push(`/chart/${patientId}`);
    }
  }, [
    chart,
    history,
    isChartLoading,
    orderId,
    pageMode,
    patientId,
    ticketPageModes,
  ]);

  return {
    prepareTicketData,
  };
}
